import { getLocalStorage, setLocalStorage } from "@/utils/cache";

export const APPEARANCE_STORAGE_KEY = "RQ_VIP_APPEARANCE";
export const FONT_SIZE_MIN = 12;
export const FONT_SIZE_MAX = 18;
export const DEFAULT_APPEARANCE = Object.freeze({
  fontSize: 14,
  theme: "light",
});

const normalizeFontSize = (value) => {
  const fontSize = Number(value);
  if (!Number.isFinite(fontSize)) {
    return DEFAULT_APPEARANCE.fontSize;
  }
  return Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, Math.round(fontSize)));
};

export const normalizeAppearance = (value = {}) => {
  const appearance = value && typeof value === "object" ? value : {};
  return {
    fontSize: normalizeFontSize(appearance.fontSize),
    theme: appearance.theme === "dark" ? "dark" : "light",
  };
};

export const getAppearance = () =>
  normalizeAppearance(getLocalStorage(APPEARANCE_STORAGE_KEY));

export const applyAppearance = (value) => {
  const appearance = normalizeAppearance(value);
  const root = document.documentElement;
  const { fontSize, theme } = appearance;
  const fontScale = Number((fontSize / DEFAULT_APPEARANCE.fontSize).toFixed(4));
  const layoutScale = Number(
    Math.min(
      1.2,
      Math.max(
        0.92,
        1 + (fontSize - DEFAULT_APPEARANCE.fontSize) * 0.05
      )
    ).toFixed(4)
  );
  const fontLevel =
    fontSize >= 17 ? "large" : fontSize >= 15 ? "medium" : "normal";

  root.dataset.appTheme = theme;
  root.dataset.fontLevel = fontLevel;
  root.setAttribute("data-vxe-ui-theme", theme);
  root.classList.toggle("dark", theme === "dark");

  root.style.setProperty("--app-font-size", `${fontSize}px`);
  root.style.setProperty("--app-font-scale", fontScale);
  root.style.setProperty("--app-layout-scale", layoutScale);
  root.style.setProperty(
    "--app-button-font-size",
    `${Math.max(12, fontSize - 2)}px`
  );
  root.style.setProperty("--el-font-size-base", `${fontSize}px`);
  root.style.setProperty("--el-font-size-medium", `${fontSize + 2}px`);
  root.style.setProperty("--el-font-size-large", `${fontSize + 4}px`);
  root.style.setProperty("--el-font-size-extra-large", `${fontSize + 6}px`);
  root.style.setProperty("--el-font-size-small", `${Math.max(12, fontSize - 1)}px`);
  root.style.setProperty(
    "--el-font-size-extra-small",
    `${Math.max(11, fontSize - 2)}px`
  );
  root.style.setProperty("--vxe-ui-font-size-default", `${fontSize}px`);
  root.style.setProperty("--vxe-ui-font-size-medium", `${fontSize}px`);
  root.style.setProperty(
    "--vxe-ui-font-size-small",
    `${Math.max(12, fontSize - 1)}px`
  );
  root.style.setProperty(
    "--vxe-ui-font-size-mini",
    `${Math.max(11, fontSize - 2)}px`
  );

  window.dispatchEvent(
    new CustomEvent("app-appearance-change", { detail: appearance })
  );

  return appearance;
};

export const saveAppearance = (value) => {
  const appearance = applyAppearance(value);
  setLocalStorage(APPEARANCE_STORAGE_KEY, appearance);
  return appearance;
};

export const initAppearance = () => applyAppearance(getAppearance());
